import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkle, GoogleLogo, CircleNotch, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginData, setLoginData] = useState({ email: "admin@citelytics.io", password: "admin123" });
  const [regData, setRegData] = useState({ name: "", email: "", password: "" });

  const submit = async (fn) => {
    setError("");
    setLoading(true);
    try {
      await fn();
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grain flex min-h-screen bg-background">
      {/* Left brand panel */}
      <div className="relative hidden w-1/2 flex-col justify-between border-r border-border bg-[#0c0c0f] p-12 lg:flex">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-action">
            <Sparkle size={18} weight="fill" className="text-white" />
          </div>
          <span className="font-heading text-lg font-extrabold tracking-tighter">Citelytics</span>
        </div>
        <div className="relative z-[1] max-w-md">
          <p className="overline text-action mb-4">LLM Citation Intelligence</p>
          <h1 className="font-heading text-5xl font-extrabold leading-none tracking-tighter">
            Track how often the models mention <span className="text-action">your brand.</span>
          </h1>
          <p className="mt-6 leading-relaxed text-muted-foreground">
            Automated, scheduled probes across GPT, Claude and Gemini. Watch your share of voice
            trend over time in one dense, beautiful dashboard.
          </p>
          <div className="mt-10 flex gap-6">
            {[
              { c: "#10A37F", n: "GPT" },
              { c: "#D97757", n: "Claude" },
              { c: "#8E24AA", n: "Gemini" },
            ].map((p) => (
              <div key={p.n} className="flex items-center gap-2 text-sm text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: p.c }} /> {p.n}
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <TrendUp size={14} /> Citation trends updated on your schedule
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Client Portal</h2>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your citation workspace.</p>

          <Button
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-button"
            className="mt-6 w-full gap-2 border-border bg-secondary/40 hover:bg-secondary"
          >
            <GoogleLogo size={18} weight="bold" /> Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 bg-secondary/50">
              <TabsTrigger value="login" data-testid="tab-login">Sign in</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-5 space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="login-email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  data-testid="login-password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                />
              </div>
              <Button
                className="w-full bg-action text-white hover:bg-action/90"
                data-testid="login-submit"
                disabled={loading}
                onClick={() => submit(() => login(loginData.email, loginData.password))}
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Sign in"}
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Demo: admin@citelytics.io / admin123
              </p>
            </TabsContent>

            <TabsContent value="register" className="mt-5 space-y-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  data-testid="register-name"
                  value={regData.name}
                  onChange={(e) => setRegData({ ...regData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  data-testid="register-email"
                  value={regData.email}
                  onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  data-testid="register-password"
                  value={regData.password}
                  onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                />
              </div>
              <Button
                className="w-full bg-action text-white hover:bg-action/90"
                data-testid="register-submit"
                disabled={loading}
                onClick={() => submit(() => register(regData.name, regData.email, regData.password))}
              >
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Create account"}
              </Button>
            </TabsContent>
          </Tabs>

          {error && (
            <p className="mt-4 text-sm text-destructive" data-testid="auth-error">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
